import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { toast, toastError } from '@/components/ui/toast';
import { useCustodialPanelState } from '@/features/custodial';
import { coinToPair, useTokenPairStore } from '@/features/tokenPairs';
import type { useTradeExecution } from '@/features/wallet';
import { useSpeedBackend } from '@/features/wallet';
import {
    AGENT_REACTIVATION_REQUIRED_CODE,
    CORE_COLLATERAL_INSUFFICIENT_CODE,
    HIP3_ACTIVATION_REQUIRED_CODE,
    type AgentStatusResponse,
} from '@/lib/api/speed';
import { getErrorMessage, isApiError } from '@/lib/errors';
import { queryKeys } from '@/lib/query';

import { soundEngine } from '../services/audio';
import {
    coinKey,
    MIN_MEANINGFUL_USD,
    selectEffectiveMaxCollateral,
    selectEffectiveMinCollateral,
    useTradingStore,
} from '../store';
import type { PositionType } from '../types';
import { markLocalLimitCancel } from '../utils/localLimitCancels';
import {
    isTriggerReachable,
    triggerRequestFields,
} from '../utils/triggerSetting';

import { useMaintenanceRate } from './useMaintenanceRate';
import { useTradeCloseVisuals } from './useTradeCloseVisuals';

interface BalanceActions {
    refetch: () => void;
}

interface UseRealTradeActionsParams {
    tradeExecution: ReturnType<typeof useTradeExecution>;
    /** Speed-trading balance with a refetch trigger (replaces on-chain reads). */
    balance: BalanceActions;
    isolatedOnly: boolean;
}

interface UseRealTradeActionsResult {
    handleOpenPosition: (type: PositionType, amount: number) => void;
    handleClosePosition: () => void;
    /** Drop the charted market's resting limit order. */
    handleCancelLimitOrder: () => void;
    /** Take the charted market's resting limit order at market ("fill now"). */
    handleFillLimitOrder: () => void;
}

/** A typed limit price is only usable as a positive, finite number. */
function parseLimitPrice(raw: string): number | null {
    const value = Number.parseFloat(raw.trim());
    return Number.isFinite(value) && value > 0 ? value : null;
}

function formatUsd(value: number): string {
    return value.toFixed(2);
}

/**
 * The backend now FAILS FAST when a requested TP/SL trigger order can't be
 * placed: it auto-closes the just-opened position for safety and returns an
 * `*Unavailable`-style error (e.g. `stopLossUnavailable`). Detect any such
 * trigger-placement failure — from an ApiError `code`/message or a BusinessError
 * message — so we can tell the user their position was closed and let them
 * retry themselves. This is SPEED trading: never a silent/background retry.
 */
function isTriggerPlacementFailure(error: unknown): boolean {
    const code = isApiError(error) ? (error.body?.code ?? '') : '';
    const codeNorm = code.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (
        codeNorm.includes('unavailable') &&
        (codeNorm.includes('stop') ||
            codeNorm.includes('profit') ||
            codeNorm.includes('trigger'))
    ) {
        return true;
    }

    // Fallback for BusinessError / uncoded errors: match on the message text.
    const msg = getErrorMessage(error).toLowerCase();
    return (
        msg.includes('unavailable') &&
        (msg.includes('stop') ||
            msg.includes('take profit') ||
            msg.includes('take-profit') ||
            msg.includes('takeprofit') ||
            msg.includes('trigger'))
    );
}

/**
 * Trade action handlers for the Hyperliquid backend flow. Opening and closing
 * are each a single authenticated POST that the backend executes gaslessly;
 * there is no FE-side signing, calldata, or approval step anymore. Insufficient
 * in-system balance opens the deposit panel instead of opening a position.
 */
export function useRealTradeActions({
    tradeExecution,
    balance,
    isolatedOnly,
}: UseRealTradeActionsParams): UseRealTradeActionsResult {
    const backend = useSpeedBackend();
    const isBrowserWallet = backend?.kind === 'v2';
    const { fireCloseVisuals } = useTradeCloseVisuals();
    const queryClient = useQueryClient();

    const networkId = useTokenPairStore((s) => s.networkId);
    const selectedCoin = useTokenPairStore((s) => s.selectedCoin);

    // The market's maintenance-margin fraction at this order's notional tier.
    // A stop loss is only real protection if it sits IN FRONT of where the
    // exchange force-closes the position, and that boundary depends on it —
    // the reported PONS short liquidated with a 50% stop armed behind it.
    const betAmount = useTradingStore((s) => s.betAmount);
    const formLeverage = useTradingStore((s) => s.leverage);
    const maintenanceRate = useMaintenanceRate(
        betAmount > 0 ? betAmount * formLeverage : null,
    );

    // Instant-or-error mapping for the backend's fail-fast error codes. Returns
    // true so useTradeExecution suppresses its generic error toast. We NEVER
    // auto-retry or auto-resubmit — the user re-initiates once the issue is fixed.
    const handleTradeError = useCallback(
        (error: unknown): boolean => {
            // 1) Pruned/expired agent caught at order time (the status read does
            //    NOT reflect a prune, e.g. after a full withdrawal). The order was
            //    NOT placed — surface the "Reactivate perps wallet" banner (by
            //    seeding the shared agent-status cache) + an explanatory toast, and
            //    leave the user in a ready state to open again after reactivating.
            if (
                isApiError(error) &&
                error.body?.code === AGENT_REACTIVATION_REQUIRED_CODE
            ) {
                queryClient.setQueryData<AgentStatusResponse>(
                    queryKeys.speed.agentStatus(),
                    (prev) => ({
                        ...prev,
                        agentAddress: prev?.agentAddress ?? null,
                        validUntil: prev?.validUntil ?? null,
                        needsReactivation: true,
                    }),
                );
                toast.error(
                    'Your perps wallet needs a one-time reactivation before trading. Your order was not placed. Tap "Reactivate perps wallet", then try again.',
                    { duration: 10000 },
                );
                return true;
            }

            // 1b) First open on a HIP-3 builder market before the one-time
            //     account activation. The order was NOT placed — surface the
            //     "Activate HIP-3 markets" banner (by seeding the shared
            //     agent-status cache) + an explanatory toast; the user re-opens
            //     after activating.
            if (
                isApiError(error) &&
                error.body?.code === HIP3_ACTIVATION_REQUIRED_CODE
            ) {
                // V2 (browser wallet): the unified account is part of the one approval its banner offers
                // ("Approve trading once."), so that banner is what the seed raises and the toast names.
                if (isBrowserWallet) {
                    queryClient.setQueryData<AgentStatusResponse>(
                        queryKeys.speed.agentStatus(),
                        (prev) => ({
                            agentAddress: null,
                            validUntil: null,
                            ...prev,
                            needsReactivation: true,
                            requiresHip3Activation: true,
                        }),
                    );
                    toast.error(
                        'This market needs a one-time approval before your first trade. Your order was not placed. Tap "Approve", then try again.',
                        { duration: 10000 },
                    );
                    return true;
                }
                queryClient.setQueryData<AgentStatusResponse>(
                    queryKeys.speed.agentStatus(),
                    (prev) => ({
                        agentAddress: null,
                        validUntil: null,
                        needsReactivation: false,
                        ...prev,
                        requiresHip3Activation: true,
                    }),
                );
                toast.error(
                    'This market needs a one-time activation before your first trade. Your order was not placed. Tap "Activate", then try again.',
                    { duration: 10000 },
                );
                return true;
            }

            // 1c) Part of the balance is still on HyperEVM (custodial/adapter) and the fast open
            //     cannot spend it. The order was NOT placed; the backend message already says how
            //     much is tradeable right now.
            if (
                isApiError(error) &&
                error.body?.code === CORE_COLLATERAL_INSUFFICIENT_CODE
            ) {
                void queryClient.invalidateQueries({
                    queryKey: queryKeys.speed.balance(),
                });
                toast.error(
                    typeof error.body?.message === 'string' &&
                        error.body.message.length > 0
                        ? error.body.message
                        : 'Part of your balance is not on HyperCore yet. Lower the margin or deposit first. Your order was not placed.',
                    { duration: 10000 },
                );
                return true;
            }

            // 2) A requested TP/SL trigger couldn't be placed, so the backend
            //    auto-closed the just-opened position for safety. Tell the user
            //    plainly and leave them in a clean, ready-to-open state — no retry.
            if (isTriggerPlacementFailure(error)) {
                toast.error(
                    "Couldn't place your stop-loss/take-profit, your position was closed for safety. Try again.",
                    { duration: 10000 },
                );
                return true;
            }

            return false;
        },
        [queryClient, isBrowserWallet],
    );

    const invalidateTrades = useCallback(() => {
        void queryClient.invalidateQueries({
            queryKey: queryKeys.trades.history(networkId),
        });
        void queryClient.invalidateQueries({
            queryKey: queryKeys.trades.active(networkId),
        });
        // a close or a limit cancel is exactly when a remainder can be left
        // behind on the exchange with no row to explain it.
        void queryClient.invalidateQueries({
            queryKey: queryKeys.trades.exposure(networkId),
        });
        void queryClient.invalidateQueries({
            queryKey: queryKeys.speed.balance(),
        });
    }, [queryClient, networkId]);

    const handleOpenPosition = useCallback(
        (type: PositionType, amount: number) => {
            const s = useTradingStore.getState();
            const isRealMode = s.runtimeMode === 'real';

            if (s.activePosition && !isRealMode) return;

            if (isRealMode && s.resumeUnverified) {
                toast.error(
                    'Trade state is currently unverified. Please wait for reconciliation to recover before opening a new position.',
                    { duration: 8000 },
                );
                return;
            }

            const minCollateralUsd = selectEffectiveMinCollateral(s);
            const maxCollateralUsd = selectEffectiveMaxCollateral(s);
            if (
                minCollateralUsd !== null &&
                maxCollateralUsd !== null &&
                minCollateralUsd > maxCollateralUsd
            ) {
                toast.error(
                    `At ${s.leverage}x, minimum collateral is $${formatUsd(
                        minCollateralUsd,
                    )}, but the current max is $${formatUsd(maxCollateralUsd)}.`,
                    { duration: 8000 },
                );
                return;
            }

            if (
                amount > 0 &&
                minCollateralUsd !== null &&
                amount < minCollateralUsd
            ) {
                toast.error(
                    `Minimum ${s.leverage}x collateral is $${formatUsd(
                        minCollateralUsd,
                    )}.`,
                    { duration: 8000 },
                );
                return;
            }

            if (maxCollateralUsd !== null && amount > maxCollateralUsd) {
                toast.error(
                    `Maximum ${s.leverage}x collateral is $${formatUsd(
                        maxCollateralUsd,
                    )}.`,
                    { duration: 8000 },
                );
                return;
            }

            // The ROI presets persist across markets, so a 300% take profit
            // armed on BTC at 30x (a 10% price move) becomes a 100% price
            // move on a 3x pair. A short cannot gain that: the price would
            // have to reach $0, and the backend rejects the trigger. Refuse
            // here, before anything is sent. The long side has the mirror
            // problem on stop loss, which the presets cannot reach today.
            if (!isTriggerReachable(s.takeProfit, s.leverage, type, true)) {
                toast.error(
                    `A ${s.takeProfit} take profit is not reachable on a ${s.leverage}x short (the price would have to fall to $0). Lower the take profit or raise the leverage.`,
                    { duration: 8000 },
                );
                return;
            }
            // A stop BEHIND the liquidation price never fires: the exchange
            // closes the position first, and the user believed they were
            // protected. The backend rejects it as `triggerUnreachable`; refuse
            // it here so nothing is sent.
            if (
                !isTriggerReachable(
                    s.stopLoss,
                    s.leverage,
                    type,
                    false,
                    maintenanceRate,
                )
            ) {
                toast.error(
                    `A ${s.stopLoss} stop loss sits past the liquidation price on a ${s.leverage}x ${type === 'LONG' ? 'long' : 'short'}, so it could never fire. Lower the stop loss, or set it as a price.`,
                    { duration: 8000 },
                );
                return;
            }

            // Market or limit. Everything above this point (collateral bounds,
            // ROI reachability) is about the SIZE and the triggers, which both
            // order types share; only the entry differs.
            const isLimit = s.orderType === 'limit';
            const limitPrice = isLimit ? parseLimitPrice(s.limitPrice) : null;
            if (isLimit && limitPrice === null) {
                toast.error(
                    'Enter a limit price above zero to place a limit order.',
                    { duration: 8000 },
                );
                return;
            }
            // A limit that is already through the market is legal (it is how you
            // ask for a fill no worse than X), it just will not rest. Say so
            // rather than blocking it.
            if (limitPrice !== null && s.currentValue > 0) {
                const crosses =
                    type === 'LONG'
                        ? limitPrice >= s.currentValue
                        : limitPrice <= s.currentValue;
                if (crosses) {
                    toast.info(
                        'This limit price crosses the market, it will fill right away.',
                        { duration: 6000 },
                    );
                }
            }

            if (isRealMode) {
                // not enough USDC in the speed-trading system → prompt a deposit.
                // thresholded like the banner: a sub-cent gap is float dust and
                // would open the deposit panel prefilled with $0.00.
                const shortfall = Math.max(0, amount - s.balance);
                if (shortfall >= MIN_MEANINGFUL_USD) {
                    useCustodialPanelState.getState().openDeposit(shortfall);
                    return;
                }

                // No `s.address` guard: the open request below is address-free
                // (the backend derives the account from the JWT), and runtime
                // mode no longer requires a connected wallet. Gating on it made
                // every click a silent no-op during AppKit's async wallet
                // rehydration after a reload.
                if (!backend) {
                    toast.error(
                        'Your session is still connecting. Try again in a moment.',
                    );
                    return;
                }

                // Either an ROI offset or an absolute price, never both — the
                // backend takes the explicit price over the bps when it gets
                // both, and validates the side and tick rounding itself.
                const takeProfitField = triggerRequestFields(
                    s.takeProfit,
                    s.leverage,
                    'takeProfit',
                );
                const stopLossField = triggerRequestFields(
                    s.stopLoss,
                    s.leverage,
                    'stopLoss',
                );

                if (isLimit && limitPrice !== null) {
                    // No slippageBps: a limit order has none, the price IS the
                    // bound. The TP/SL legs go over exactly as the market path
                    // sends them; the backend resolves a bps leg against the
                    // rounded limit price instead of the mark.
                    soundEngine.playOpen();
                    tradeExecution.executeLimitOpen({
                        backend,
                        request: {
                            coin: selectedCoin,
                            isLong: type === 'LONG',
                            collateralUsd: amount,
                            leverage: s.leverage,
                            limitPrice: String(limitPrice),
                            ...takeProfitField,
                            ...stopLossField,
                        },
                        positionType: type,
                        positionAmount: amount,
                        onSuccess: ({ status }) => {
                            balance.refetch();
                            invalidateTrades();
                            if (status === 'Resting') {
                                toast.success('Limit order placed', {
                                    duration: 6000,
                                });
                            }
                        },
                        onError: handleTradeError,
                    });
                    return;
                }

                soundEngine.playOpen();
                tradeExecution.executeOpen({
                    backend,
                    request: {
                        coin: selectedCoin,
                        isLong: type === 'LONG',
                        collateralUsd: amount,
                        leverage: s.leverage,
                        slippageBps: s.slippageBps,
                        ...takeProfitField,
                        ...stopLossField,
                    },
                    positionType: type,
                    positionAmount: amount,
                    snapshotEntryPrice: s.currentValue,
                    isolatedOnly,
                    onSuccess: () => {
                        balance.refetch();
                        invalidateTrades();
                    },
                    onError: handleTradeError,
                });
                return;
            }

            // Defense in depth: the amount arrives sized against `s.balance`, which
            // is NOT always the demo balance — while trading authority is still
            // resolving the store carries the REAL custodial number. If the sizing
            // path and the balance source drift apart again, refuse the open rather
            // than mint demo money (the store also floors the debit at 0).
            if (amount > s.demoBalance) {
                toast.error(
                    `Your demo balance is $${formatUsd(s.demoBalance)}. Lower the amount to open a demo position.`,
                    { duration: 8000 },
                );
                return;
            }

            const demoKey = coinKey(selectedCoin);
            if (demoKey !== null && s.demoRestingOrders[demoKey]) {
                toast.error(
                    'You already have a limit order on this market. Cancel it first.',
                    { duration: 8000 },
                );
                return;
            }

            if (isLimit && limitPrice !== null) {
                // Demo has no backend to rest the order on, so the store holds
                // it and useDemoLimitFills fills it when the streamed price
                // crosses. The margin is debited on placement, as it is for a
                // demo market open, and refunded on cancel.
                soundEngine.playOpen();
                s.placeDemoRestingOrder(selectedCoin, {
                    type,
                    limitPrice,
                    amount,
                    leverage: s.leverage,
                    placedAt: Date.now(),
                });
                toast.success('Limit order placed', { duration: 6000 });
                return;
            }

            soundEngine.playOpen();
            s.setDemoBalance((prev) => prev - amount);
            s.setActivePosition({
                type,
                entryPrice: s.currentValue,
                timestamp: Date.now(),
                amount,
                leverage: s.leverage,
            });
        },
        [
            selectedCoin,
            tradeExecution,
            balance,
            backend,
            invalidateTrades,
            handleTradeError,
            isolatedOnly,
            maintenanceRate,
        ],
    );

    const handleClosePosition = useCallback(() => {
        const s = useTradingStore.getState();
        const isRealMode = s.runtimeMode === 'real';

        if (isRealMode) {
            // The CLOSE button belongs to the market on the chart (Option A:
            // the rail follows the chart), and the store's flat fields ARE that
            // market's slot. Reading `tradeExecution.coin` was reading whichever
            // coin the execution hook last touched, which with several positions
            // open is not necessarily the one the user is looking at.
            const coin = selectedCoin;
            if (!coin) return;
            // Same reasoning as the open path: a missing authed client is a
            // transient session state, so say so instead of eating the click on
            // a position the user is trying to get out of.
            if (!backend) {
                toast.error(
                    'Your session is still connecting. Try again in a moment.',
                );
                return;
            }

            // Snapshot the closing position BEFORE the optimistic close resets
            // execution state, so onSuccess can still play the close visual — and
            // so an AGENT_REACTIVATION_REQUIRED failure can RESTORE the still-open
            // position (the backend fails fast without touching it) instead of
            // leaving it hidden as 'failed'. openTxHash + coin round out the
            // payload hydrateFromRemoteTrade needs.
            const closing = {
                entry: s.execEntryPrice,
                posType: s.execPositionType,
                posAmount: s.execPositionAmount,
                leverage: s.execLeverage ?? 1,
                tradeHash: s.tradeHash,
                openTxHash: s.openTxHash,
                coin,
            };

            tradeExecution.executeClose({
                backend,
                coin,
                onSuccess: ({ closeMs }) => {
                    // The optimistic close already reset execution state
                    // (tradeHash → null), so the WS / reconciliation close handler
                    // no longer matches this trade and would skip BOTH the close
                    // visual and the snapshot cleanup. Do them here instead.

                    // (a) Clear the recovery snapshot so a hard refresh before REST
                    //     reconciliation can't restore this just-closed trade.

                    // (b) Play the close animation + toast directly. fireCloseVisuals
                    //     dedups on tradeHash, so a late WS Closed event that still
                    //     matched (pre-reset race) stays an idempotent no-op.
                    if (
                        closing.entry !== null &&
                        closing.posType !== null &&
                        closing.posAmount !== null
                    ) {
                        fireCloseVisuals({
                            entry: closing.entry,
                            exitPrice: useTradingStore.getState().currentValue,
                            posType: closing.posType,
                            posAmount: closing.posAmount,
                            leverage: closing.leverage,
                            closeReason: 'manual',
                            coin: closing.coin,
                            closeMs,
                            ...(closing.tradeHash
                                ? { tradeHash: closing.tradeHash }
                                : {}),
                        });
                    }

                    if (closing.tradeHash) {
                        useTradingStore
                            .getState()
                            .setPendingClosedTradeHash(closing.tradeHash);
                    }
                    balance.refetch();
                    invalidateTrades();
                },
                onError: (error) => {
                    // Seed the reactivation banner / classify the error, exactly
                    // as before.
                    const handled = handleTradeError(error);

                    // CRITICAL close-safety: on AGENT_REACTIVATION_REQUIRED the
                    // backend failed fast on a stale/pruned agent (e.g. open →
                    // withdraw prunes the agent → close) WITHOUT touching the
                    // position — it is STILL OPEN. useTradeExecution's catch sets
                    // status:'failed' AFTER this handler returns, which
                    // selectHasRealOpenTrade / selectCanClose exclude — hiding the
                    // live position and its close button until a hard refresh.
                    // Restore it to a closable 'open' on the next microtask (after
                    // that 'failed' write lands, or it is clobbered) so it stays
                    // VISIBLE + CLOSABLE. The ReactivateAgentBanner is already
                    // seeded by handleTradeError; the user reactivates, then closes
                    // again — NO auto-retry. (CLOSE path only: an OPEN failing with
                    // the same code is a valid ready-to-open 'failed', no position.)
                    const { tradeHash, posType, posAmount, entry } = closing;
                    if (
                        isApiError(error) &&
                        error.body?.code === AGENT_REACTIVATION_REQUIRED_CODE &&
                        tradeHash &&
                        posType &&
                        posAmount !== null &&
                        entry !== null
                    ) {
                        queueMicrotask(() => {
                            tradeExecution.hydrateFromRemoteTrade({
                                tradeHash,
                                openTxHash: closing.openTxHash,
                                coin,
                                side: posType,
                                amount: posAmount,
                                leverage: closing.leverage,
                                entry,
                                pairSymbol: coinToPair(coin),
                                createdAt: new Date().toISOString(),
                            });
                        });
                    }

                    return handled;
                },
            });
            return;
        }

        if (!s.activePosition) return;

        const closedTrade = fireCloseVisuals({
            entry: s.activePosition.entryPrice,
            exitPrice: s.currentValue,
            posType: s.activePosition.type,
            posAmount: s.activePosition.amount,
            leverage: s.activePosition.leverage,
            // Demo rows keep the market they were traded on — the history list
            // used to label them with whatever pair happened to be selected.
            coin: selectedCoin,
        });

        if (closedTrade) {
            const returnAmount = Math.max(
                0,
                s.activePosition.amount + closedTrade.pnlValue,
            );
            s.setDemoBalance((prev) => prev + returnAmount);
            s.addTrade(closedTrade);
        }
        s.setActivePosition(null);
    }, [
        selectedCoin,
        tradeExecution,
        balance,
        backend,
        fireCloseVisuals,
        invalidateTrades,
        handleTradeError,
    ]);

    /**
     * Cancel or "fill now" the charted market's resting limit order.
     *
     * One implementation: the two differ only in the execution call and in the
     * copy, and both can legitimately come back `Open` — a GTC can fill while
     * the request is in flight, which is a position the user now holds, not an
     * error.
     */
    const runLimitAction = useCallback(
        (action: 'cancel' | 'fill') => {
            const s = useTradingStore.getState();
            const coin = selectedCoin;
            if (!coin) return;

            if (s.runtimeMode !== 'real') {
                if (action === 'cancel') {
                    s.cancelDemoRestingOrder(coin);
                    toast.success('Limit order cancelled', { duration: 6000 });
                } else {
                    s.fillDemoRestingOrder(coin);
                    toast.success('Limit order filled, position is running', {
                        duration: 6000,
                    });
                }
                return;
            }

            if (!backend) {
                toast.error(
                    'Your session is still connecting. Try again in a moment.',
                );
                return;
            }

            // captured before the call: a successful cancel clears the slot, so
            // the hash would be gone by the time we need to mark it as ours.
            const restingHash = s.tradeHash;
            // "Fill now" cancels the resting order server-side before it opens
            // at market, and that cancel is published like any other. Claim it
            // up front, or the user is told their own Fill now was a
            // cancellation (see localLimitCancels).
            if (action === 'fill') markLocalLimitCancel(restingHash);
            const params = {
                backend,
                coin,
                onSuccess: ({ status }: { status: 'Cancelled' | 'Open' }) => {
                    balance.refetch();
                    invalidateTrades();
                    if (status === 'Open') {
                        toast.success(
                            'Limit order filled, position is running',
                            { duration: 8000 },
                        );
                        return;
                    }
                    markLocalLimitCancel(restingHash);
                    toast.success('Limit order cancelled', { duration: 6000 });
                },
                onError: (error: unknown) => {
                    // The order is gone (filled, swept, already cancelled): the
                    // lists are what tell the user what they actually hold now.
                    invalidateTrades();
                    toastError(error, {
                        operation:
                            action === 'cancel' ? 'Cancel order' : 'Fill order',
                    });
                    return true;
                },
            };

            if (action === 'cancel') {
                tradeExecution.executeLimitCancel(params);
            } else {
                tradeExecution.executeLimitFill(params);
            }
        },
        [selectedCoin, backend, tradeExecution, balance, invalidateTrades],
    );

    const handleCancelLimitOrder = useCallback(
        () => runLimitAction('cancel'),
        [runLimitAction],
    );
    const handleFillLimitOrder = useCallback(
        () => runLimitAction('fill'),
        [runLimitAction],
    );

    return {
        handleOpenPosition,
        handleClosePosition,
        handleCancelLimitOrder,
        handleFillLimitOrder,
    };
}
